import styled, { keyframes } from "styled-components";

const BRAND = {
  navy: "#293461",
  teal: "#00838c",
  muted: "#949ab0",
  navyLight: "rgba(41,52,97,0.08)",
  tealLight: "rgba(0,131,140,0.10)",
  tealGlow: "rgba(0,131,140,0.18)",
  border: "#e8eaf0",
  surface: "#f7f8fc",
  white: "#ffffff",
  danger: "#e05252",
  success: "#2ecc71",
};

export const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const Container = styled.div`
  padding: 20px 24px 40px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  background: ${BRAND.surface};
  min-height: 100vh;
  animation: ${fadeIn} 0.4s ease both;

  & .loader {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 200px;
  }

  @media (max-width: 640px) {
    padding: 12px 12px 32px;
    gap: 14px;
  }
`;

export const FilterBarContainer = styled.div`
  display: flex;
  position: fixed;
  height: 100vh;
  flex-direction: column;
  align-items: flex-start;
  width: 280px;
  font-size: 14px;
  padding: 24px 20px 100px;
  background: ${BRAND.white};
  box-shadow: 4px 0 20px rgba(41, 52, 97, 0.08);
  overflow-y: auto;
  z-index: 100;

  @media (max-width: 1024px) {
    width: 50%;
    z-index: 200;
  }
  @media (max-width: 700px) {
    width: 100%;
    z-index: 200;
  }
`;

export const SearchBarContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 40px;
  border: 1.5px solid ${BRAND.border};
  border-radius: 10px;
  background: ${BRAND.white};
  overflow: hidden;
  transition:
    border-color 0.2s,
    box-shadow 0.2s;

  &:focus-within {
    border-color: ${BRAND.teal};
    box-shadow: 0 0 0 3px ${BRAND.tealGlow};
  }

  @media (max-width: 1024px) {
    width: 100%;
  }
`;

export const SearchBar = styled.input`
  flex: 1;
  padding: 0 12px;
  border: none;
  background: transparent;
  color: ${BRAND.navy};
  outline: none;
  font-size: 13px;
  font-weight: 600;
  font-family: openSans-SemiBold, sans-serif;

  &::placeholder {
    color: ${BRAND.muted};
    font-weight: 400;
  }
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
  background: ${BRAND.white};
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 2px 12px rgba(41, 52, 97, 0.06);
  animation: ${fadeIn} 0.35s ease both;

  @media (min-width: 641px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (min-width: 1025px) {
    grid-template-columns: 2.5fr 2.5fr 3fr 3fr 4fr;
    gap: 16px;
  }
`;

export const ContainerGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  background: ${BRAND.white};
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 2px 12px rgba(41, 52, 97, 0.06);
  animation: ${fadeIn} 0.4s ease both;
  align-items: end;

  @media (min-width: 1025px) {
    grid-template-columns: 1fr 1fr 2fr 2fr auto auto;
    gap: 16px;
  }
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

export const Label = styled.span`
  font-family: openSans-SemiBold, sans-serif;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${BRAND.muted};
  display: block;
  margin-bottom: 6px;
`;

export const Center = styled.span`
  font-family: openSans-SemiBold, sans-serif;
  font-size: 13px;
  color: ${BRAND.navy};
  cursor: pointer;
`;

export const SelectContainer = styled.div`
  display: flex;

  & .agent_row {
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 4px 0;
    gap: 10px;

    & .agent_name {
      font-size: 14px;
      font-weight: 600;
      flex: 1;
      color: ${BRAND.navy};
      font-family: openSans-SemiBold, sans-serif;
    }

    & .agent_email {
      font-size: 13px;
      color: ${BRAND.muted};
      font-family: openSans-SemiBold, sans-serif;
    }

    & .add_circle {
      width: 30px;
      height: 30px;
      border-radius: 50%;
      display: flex;
      justify-content: center;
      align-items: center;
      background-color: ${BRAND.tealLight};
      cursor: pointer;
      transition: background 0.2s;

      &:hover {
        background-color: ${BRAND.tealGlow};
      }

      & .add_icon {
        font-size: 14px;
        color: ${BRAND.teal};
      }
    }
  }
`;

export const HyperLink = styled.span`
  cursor: pointer;
  font-family: openSans-SemiBold, sans-serif;
  font-size: 13px;
  color: ${BRAND.teal};
  text-decoration: none;
  padding-bottom: 1px;
  transition:
    color 0.15s,
    border-color 0.15s;

  &:hover {
    text-decoration: underline;
    border-color: ${BRAND.navy};
  }
`;

export const Loader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 80px 0;

  & .MuiCircularProgress-colorPrimary {
    color: ${BRAND.teal};
  }
`;

export const PieIcon = styled.img`
  width: 26px;
  height: 26px;
  object-fit: contain;
  cursor: pointer;
  transition:
    transform 0.2s,
    opacity 0.2s;
  opacity: 0.8;
  align-self: flex-end;
  margin-bottom: 8px;

  &:hover {
    transform: scale(1.15);
    opacity: 1;
  }
`;

export const Circle = styled.circle`
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.85;
  }
`;

export const Tooltip = styled.div`
  position: absolute;
  left: 65%;
  top: 24%;
  background: ${BRAND.navy};
  color: ${BRAND.white};
  padding: 6px 10px;
  font-family: openSans-SemiBold, sans-serif;
  font-size: 12px;
  border-radius: 8px;
  pointer-events: none;
  white-space: nowrap;
  box-shadow: 0 4px 12px rgba(41, 52, 97, 0.2);
  z-index: 10;
`;

export const StyledInput = styled.input`
  cursor: pointer;
  accent-color: ${BRAND.teal};
`;

export const LabelValueContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 50%;
  margin-top: 20px;

  &:nth-child(even) {
    padding-left: 10px;
  }

  @media (min-width: 641px) {
    width: auto;
    margin-top: 0;
    padding-left: 0 !important;
  }
`;

export const Value = styled.span`
  font-family: openSans-SemiBold, sans-serif;
  font-size: 14px;
  line-height: 1.6;
  color: ${BRAND.navy};
  word-break: break-all;
`;

export const SectionCard = styled.div`
  background: ${BRAND.white};
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 2px 12px rgba(41, 52, 97, 0.06);
  animation: ${fadeIn} 0.4s ease both;
`;

export const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const SearchIcon = styled.div`
  width: 18px;
  height: 18px;
  margin: auto 10px;
  display: flex;
  align-items: center;
  color: ${BRAND.muted};
`;
